import { format } from "date-fns";
import { API_TBase } from "./API";
import {
  MOVE_TO_PROGRESS_STEP_HISTORICAL_TYPE_VALUE,
  REPORT_GENERATION_HISTORICAL_TYPE_VALUE,
  CASE_DECISION_HISTORICAL_TYPE_VALUE,
} from "@/constants/dataHistory";
import { ROLE_LABELS } from "@/constants/roles";

const DATETIME_FORMAT = "dd/MM/yyyy HH:mm";

const TRUE_VALUE = "true";
const FALSE_VALUE = "false";
const YES_LABEL = "Yes";
const NO_LABEL = "No";
const DEACTIVATED_LABEL = "deactivated";

export type API_THistory = {
  section_label: string;
  answer_block_label: string;
  show_answer_block_label: boolean;
  question_label: string;
  show_question_label: boolean;
  before_value: string;
  after_value: string;
  owner_id: number;
  owner: THistory_Owner;
  group_index: number;
  answer_block_index: number;
  answer_block_loop_index: number;
  historical_value_type: string;
  progress_step_before_id: number;
  progress_step_after_id: number;
  document_id: number;
  file_id: number;
  title: string;
  sub_title: string;
} & API_TBase;

export type THistory_Owner = {
  active: boolean;
  company_id: number;
  company_position: string;
  email: string;
  first_name: string;
  has_validation_passed: boolean;
  last_name: string;
  phone: string;
  role: string;
  title: string;
} & API_TBase;

export type THistory_Table_Array = {
  [key: number]: THistory_Table_Value[][];
};

export type THistory_Table_Value = {
  id: number;
  actionBy: string;
  actionByRole?: string;
  date: string;
  section: string;
  beforeValue: string;
  afterValue: string;
  groupIndex: number;
  isFirstInLoop: boolean;
  label: string;
  historicalValueType: string;
  progressStepBeforeId: number;
  progressStepAfterId: number;
  fileId: number;
  title: string;
  subTitle: string;
};

export type THistory_Table = {
  id: number;
  actionBy: string;
  actionByRole?: string;
  date: string;
  section: string;
  beforeValue: string[];
  afterValue: string[];
  isFirstInLoop: boolean[];
  label: string[];
  historicalValueType: string;
  progressStepBeforeId: number;
  progressStepAfterId: number;
  fileId: number;
  title: string;
  subTitle: string;
  rowspan?: number | string;
  colspan?: number | string;
};

const convertString = (value: string) => {
  if (value === TRUE_VALUE) return YES_LABEL;
  if (value === FALSE_VALUE) return NO_LABEL;

  return value;
};

const convertLabel = (value: THistory_Table_Array[]) => {
  return value.reduce((acc: string[], cur: any) => {
    const index = acc.indexOf(" ") === -1 ? acc.length - 1 : acc.indexOf(" ") - 1;

    const label = acc[index] === cur.label ? " " : cur.label;

    acc = [...acc, label];

    return acc;
  }, []);
};

const getDataHistoryValueLabel = (a) => {
  return a.show_question_label
    ? a.question_label
    : a.show_answer_block_label
    ? a.answer_block_label
    : " ";
};

const getDataHistoryValueActionBy = (a) => {
  const { first_name, last_name, role, DeletedAt } = a?.owner;

  if (!first_name || !last_name) return "―";

  let name = `${first_name} ${last_name}`;

  if (role) name += ` (${ROLE_LABELS[role]})`;

  if (DeletedAt) name += ` - ${DEACTIVATED_LABEL}`;

  return name;
};

const getDataHistoryValueActionByRole = (a) => {
  return a?.owner?.role;
};

const getDataHistoryValueDate = (a) => {
  const formatStr = DATETIME_FORMAT;

  const date: any = a.UpdatedAt && Date.parse(a.UpdatedAt) > 0 ? new Date(a.UpdatedAt) : null;

  return date ? format(date, formatStr) : "―";
};

export const convertApiToValues = (a: API_THistory): THistory_Table_Value => {
  return {
    id: a.ID,
    actionBy: getDataHistoryValueActionBy(a),
    actionByRole: getDataHistoryValueActionByRole(a),
    date: getDataHistoryValueDate(a),
    section: a.section_label ? a.section_label : "—",
    beforeValue: a.before_value ? a.before_value : "—",
    afterValue: a.after_value ? a.after_value : "—",
    groupIndex: a.group_index,
    isFirstInLoop: a.answer_block_index === 0 && a.answer_block_loop_index !== 0,
    label: getDataHistoryValueLabel(a),
    historicalValueType: a.historical_value_type,
    progressStepBeforeId: a.progress_step_before_id,
    progressStepAfterId: a.progress_step_after_id,
    fileId: a.file_id,
    title: a.title,
    subTitle: a.sub_title,
  };
};

const DEFAULT_COLUMN_SPAN = "0";
const DIVIDER_COLUMN_SPAN = "3";

const getColSpanFromHistoricalTypeValue = (a: THistory_Table_Value): string => {
  const types = [
    MOVE_TO_PROGRESS_STEP_HISTORICAL_TYPE_VALUE,
    REPORT_GENERATION_HISTORICAL_TYPE_VALUE,
    CASE_DECISION_HISTORICAL_TYPE_VALUE,
  ];

  if (types.includes(a.historicalValueType)) return DIVIDER_COLUMN_SPAN;

  return DEFAULT_COLUMN_SPAN;
};

export const convertValuesToTable = (a: THistory_Table_Array[]): THistory_Table => {
  const tableValue = a[0] as THistory_Table_Value;

  return {
    id: tableValue.id,
    actionBy: tableValue.actionBy,
    actionByRole: tableValue.actionByRole,
    date: tableValue.date,
    section: tableValue.section,
    label: convertLabel(a),
    beforeValue: a.map((item) => convertString((item as THistory_Table_Value).beforeValue)),
    afterValue: a.map((item) => convertString((item as THistory_Table_Value).afterValue)),
    isFirstInLoop: a.map((item) => (item as THistory_Table_Value).isFirstInLoop),
    historicalValueType: tableValue.historicalValueType,
    progressStepBeforeId: tableValue.progressStepBeforeId,
    progressStepAfterId: tableValue.progressStepAfterId,
    fileId: tableValue.fileId,
    title: tableValue.title,
    subTitle: tableValue.subTitle,
    colspan: getColSpanFromHistoricalTypeValue(tableValue),
  };
};

export const convertHistoryForTable = (a: API_THistory[]): THistory_Table[] => {
  const historyValues = a.map((a) => convertApiToValues(a));

  const indexArray = historyValues
    .map((item) => item.groupIndex)
    .filter((item, i, items) => items.indexOf(item) === i);

  const itemTest: THistory_Table_Array[][] = [];

  indexArray.forEach((index) => {
    itemTest[index] = historyValues.filter((item) => item.groupIndex === index);
  });

  return itemTest
    .filter((item) => item.length)
    .map((a) => convertValuesToTable(a))
    .sort((a, b) => {
      return a.id > b.id ? -1 : 1;
    });
};
